/**
 * Local persistence.
 *
 * Deliberately the *only* thing the slice needs to work: your room, the
 * reactions it has collected, and who you are. When the Worker is live
 * (`src/net/api.ts`) this becomes the offline cache rather than the source of
 * truth — so nothing here may assume it is authoritative.
 */

import type { RoomDef } from '../core/room'
import { decodeRoom, encodeRoom } from '../core/codec'
import type { Replay } from '../sim/replay'

const KEY = {
  player: 'toyraiders.player',
  myRoom: 'toyraiders.myRoom',
  inbox: 'toyraiders.inbox',
  seen: 'toyraiders.inboxSeen',
  lastRaided: 'toyraiders.lastRaided',
} as const

const MAX_INBOX = 12

function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return fallback
    return JSON.parse(raw) as T
  } catch {
    return fallback
  }
}

function write(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // Private-browsing / quota. The game keeps working, it just forgets.
  }
}

export interface PlayerProfile {
  name: string
}

export function loadPlayer(): PlayerProfile {
  return read<PlayerProfile>(KEY.player, { name: '' })
}

export function savePlayer(p: PlayerProfile): void {
  write(KEY.player, p)
}

export function loadMyRoom(): RoomDef | null {
  try {
    const code = localStorage.getItem(KEY.myRoom)
    return code ? decodeRoom(code) : null
  } catch {
    return null
  }
}

export function saveMyRoom(room: RoomDef): void {
  try {
    localStorage.setItem(KEY.myRoom, encodeRoom(room))
  } catch {
    /* ignore */
  }
}

/** Reactions to *your* room, newest first. */
export function loadInbox(): Replay[] {
  return read<Replay[]>(KEY.inbox, [])
}

export function pushInbox(replay: Replay): void {
  const all = [replay, ...loadInbox()].slice(0, MAX_INBOX)
  write(KEY.inbox, all)
}

export function unseenCount(): number {
  const seen = read<number>(KEY.seen, 0)
  return loadInbox().filter((r) => r.createdAt > seen).length
}

export function markInboxSeen(): void {
  write(KEY.seen, Date.now())
}

/** The last room you raided — powers "再来一次" and "复仇". */
export function rememberRaided(room: RoomDef): void {
  try {
    localStorage.setItem(KEY.lastRaided, encodeRoom(room))
  } catch {
    /* ignore */
  }
}

export function loadLastRaided(): RoomDef | null {
  try {
    const code = localStorage.getItem(KEY.lastRaided)
    return code ? decodeRoom(code) : null
  } catch {
    return null
  }
}
